import React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import SwipeableViews from 'react-swipeable-views';
import { withStyles } from '@material-ui/core/styles';

import PlotMany from 'hcla-web-frontend-primitives/app/components/primitives/Plot/PlotMany';
import CircularIndeterminate from 'hcla-web-frontend-primitives/app/components/primitives/Progress/CircularIndeterminate';

import CanvasPaper from '../../../primitives/CanvasPaper';
import StatsTable from './StatsTable';
import { getDistNormalPercentile, getDistNormalMeanStddev } from './distributions/normal';
import { getDistLognormalMeanErrorFactor, getDistLognormalPercentile, getDistLognormalMedianErrorFactor } from './distributions/lognormal';

const styles = () => ({
  plot: {
    width: '100%',
    height: '100%',
  },
  tabContainer: {
    width: '100%',
    height: 'calc(100% - 4px)',
  },
  tabsRoot: {
    height: 'calc(100% - 48px)',
    '& .react-swipeable-view-container': {
      height: '100%',
    },
  },
  textMessage: {
    margin: 'auto',
  },
  messageContainer: {
    display: 'flex',
    height: '100%',
  },
});

class ResultsCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: 0,
    };
  }

  getPriorDistributions = () => {
    const {
      priorDist, priorType, priorValues, points,
    } = this.props;
    const defaultVals = {
      X: [],
      pdf: [],
      cdf: [],
      mean: NaN,
      median: NaN,
      variance: NaN,
    };

    try {
      if (priorValues === null || priorValues[0] === undefined || priorValues[1] === undefined) {
        return defaultVals;
      }

      if (Number.isNaN(priorValues[0]) || Number.isNaN(priorValues[1])) {
        return defaultVals;
      }

      switch (priorDist) {
        case 'Normal':
          switch (priorType) {
            case 'Mean & Standard-Deviation': return getDistNormalMeanStddev(priorValues[0], priorValues[1], points);
            case 'Percentile': return getDistNormalPercentile(priorValues[0], priorValues[1], points);
            default: return defaultVals;
          }

        case 'Log-Normal':
          switch (priorType) {
            case 'Percentile': return getDistLognormalPercentile(priorValues[0], priorValues[1], points);
            case 'Mean & Error-Factor': return getDistLognormalMeanErrorFactor(priorValues[0], priorValues[1], points);
            case 'Median & Error-Factor': return getDistLognormalMedianErrorFactor(priorValues[0], priorValues[1], points);
            case 'Median & Error-Factor (estimates)':
              if (!Number.isNaN(priorValues[2])) {
                return getDistLognormalMedianErrorFactor(priorValues[0], priorValues[2], points);
              }
              return defaultVals;
            default: return defaultVals;
          }

        default: return defaultVals;
      }
    } catch (e) {
      console.error(e);
      return defaultVals;
    }
  };

  handleChange = (event, value) => {
    this.setState({ value });
  };

  handleChangeIndex = (index) => {
    this.setState({ value: index });
  };

  noResultsCard = () => {
    const { classes } = this.props;
    return (
      <CanvasPaper>
        <div className={classes.messageContainer} >
          <Typography variant="h5" className={classes.textMessage} >
            No quantification results available.
          </Typography>
        </div>
      </CanvasPaper>
    );
  };

  plotHomogeneousPDF = (priorDist) => {
    const axesOptions = {
      xTitle: 'X',
      yTitle: 'Probability Density',
      xScaleType: this.isModelTypeLognormal() ? 'log' : 'linear',
      yScaleType: 'linear',
    };

    return (
      <PlotMany
        data={[
          {
            x: priorDist.X,
            y: priorDist.pdf,
            title: 'Prior',
            color: this.props.theme.palette.secondary.main,
          },
          {
            x: this.props.data.result_data.x,
            y: this.props.data.result_data.full_pdf,
            title: 'Posterior',
            color: this.props.theme.palette.primary.light,
          },
        ]}
        axesOptions={axesOptions}
        showLegend
      />
    );
  };

  plotNonHomogeneousPDF = (priorDist) => {
    const axesOptions = {
      xTitle: 'X',
      yTitle: 'Probability Density',
      xScaleType: this.isModelTypeLognormal() ? 'log' : 'linear',
      yScaleType: 'linear',
    };

    return (
      <PlotMany
        data={[
          {
            x: priorDist.X,
            y: priorDist.pdf,
            title: 'Prior',
            color: this.props.theme.palette.secondary.main,
          },
          {
            x: this.props.data.result_data.pdf_mean.x,
            y: this.props.data.result_data.pdf_mean.y,
            title: 'Posterior Median',
            color: this.props.theme.palette.primary.light,
          },
        ]}
        axesOptions={axesOptions}
        showLegend
      />
    );
  };

  plotNonHomogeneousCDF = (priorDist) => {
    const { data, theme } = this.props;

    const axesOptions = {
      xTitle: 'X',
      yTitle: 'Probability',
      xScaleType: this.isModelTypeLognormal() ? 'log' : 'linear',
      yScaleType: 'linear',
    };

    return (
      <PlotMany
        data={[
          {
            x: priorDist.X,
            y: priorDist.cdf,
            title: 'Prior',
            color: theme.palette.secondary.main,
          },
          {
            x: data.result_data.cdf_lower.x,
            y: data.result_data.cdf_lower.y,
            title: 'Posterior Lower',
            color: '#606191',
          },
          {
            x: data.result_data.cdf_median.x,
            y: data.result_data.cdf_median.y,
            title: 'Posterior Median',
            color: '#6e7a66',
          },
          {
            x: data.result_data.cdf_upper.x,
            y: data.result_data.cdf_upper.y,
            color: '#c99f4c',
            title: 'Posterior Upper',
          },
          {
            x: data.result_data.cdf_mean.x,
            y: data.result_data.cdf_mean.y,
            title: 'Posterior Mean',
            color: this.props.theme.palette.primary.light,
          },
        ]}
        axesOptions={axesOptions}
        showLegend
      />
    );
  };

  plotHomogeneousCDF = (priorDist) => {
    const axesOptions = {
      xTitle: 'X',
      yTitle: 'Probability',
      xScaleType: this.isModelTypeLognormal() ? 'log' : 'linear',
      yScaleType: 'linear',
    };

    return (
      <PlotMany
        data={[
          {
            x: priorDist.X,
            y: priorDist.cdf,
            title: 'Prior',
            color: this.props.theme.palette.secondary.main,
          },
          {
            x: this.props.data.result_data.x,
            y: this.props.data.result_data.full_cdf,
            title: 'Posterior',
            olor: this.props.theme.palette.primary.light,
          },
        ]}
        axesOptions={axesOptions}
        showLegend
      />
    );
  };

  statsHomogeneous = (priorDist) => {
    const { data } = this.props;
    const list = [
      {
        name: 'Mean',
        prior: priorDist.mean,
        posterior: data.result_data.mean,
      },
      {
        name: 'Median',
        prior: priorDist.median,
        posterior: data.result_data.median,
      },
      {
        name: 'Variance',
        prior: priorDist.variance,
        posterior: data.result_data.variance,
      },
    ];
    return (<StatsTable items={list} />);
  };

  statsNonHomogeneous = (priorDist) => {
    const { data } = this.props;
    const list = [
      {
        name: 'Lower Bound (5th Percentile)',
        prior: NaN,
        posterior: data.result_data.lower_bound,
      },
      {
        name: 'Upper Bound (95th Percentile)',
        prior: NaN,
        posterior: data.result_data.upper_bound,
      },
      {
        name: 'Population Variability Mean Mean',
        prior: NaN,
        posterior: data.result_data.population_variability_mean_mean,
      },
      {
        name: 'Population Variability Variance Mean',
        prior: NaN,
        posterior: data.result_data.population_variability_variance_mean,
      },
    ];
    return (<StatsTable items={list} />);
  };

  isModelTypeHomogeneous = () => {
    return this.props.data.model_type === 'Homogeneous';
  };

  isModelTypeLognormal = () => {
    return this.props.priorDist === 'Log-Normal';
  };

  render() {
    const {
      classes, theme, data, isLoading, isQuantifying,
    } = this.props;

    if (isLoading || isQuantifying) {
      return (
        <CanvasPaper>
          <CircularIndeterminate size={80} color="secondary" />
        </CanvasPaper>
      );
    }

    if (data === null || data.result_data === null) {
      return this.noResultsCard();
    }

    const priorDist = this.getPriorDistributions();
    const homogeneous = this.isModelTypeHomogeneous();
    let pdfPlot,
      cdfPlot,
      statsTab;

    if (homogeneous) {
      pdfPlot = this.plotHomogeneousPDF(priorDist);
      cdfPlot = this.plotHomogeneousCDF(priorDist);
      statsTab = this.statsHomogeneous(priorDist);
    } else {
      pdfPlot = this.plotNonHomogeneousPDF(priorDist);
      cdfPlot = this.plotNonHomogeneousCDF(priorDist);
      statsTab = this.statsNonHomogeneous(priorDist);
    }

    return (
      <CanvasPaper>
        <Tabs
          value={this.state.value}
          onChange={this.handleChange}
          indicatorColor="secondary"
          textColor="primary"
          fullWidth
          centered
        >
          <Tab label="Stats" />
          <Tab label="PDF" />
          <Tab label="CDF" />
        </Tabs>
        <SwipeableViews
          className={classes.tabsRoot}
          slideClassName={classes.tabContainer}
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={this.state.value}
          onChangeIndex={this.handleChangeIndex}
        >
          <div className={classes.tabContainer}>
            {statsTab}
          </div>
          <div className={classes.tabContainer}>
            {pdfPlot}
          </div>
          <div className={classes.tabContainer}>
            {cdfPlot}
          </div>
        </SwipeableViews>
      </CanvasPaper>
    );
  }
}

ResultsCard.defaultProps = {
  data: null,
  points: 5,
};

ResultsCard.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
    model: PropTypes.number.isRequired,
    model_title: PropTypes.string.isRequired,
    model_type: PropTypes.string.isRequired,
    date_created: PropTypes.string.isRequired,
    creator: PropTypes.number.isRequired,
    result_data: PropTypes.instanceOf(Object),
  }),
  points: PropTypes.number,
  isQuantifying: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  priorType: PropTypes.string.isRequired,
  priorDist: PropTypes.string.isRequired,
  priorValues: PropTypes.arrayOf(Number).isRequired,
};

export default withStyles(styles, { withTheme: true })(ResultsCard);

